<template>
  <!--树形组件（支持同步、异步）-->
  <el-tree
    ref="tree"
    :data="data"
    lazy
    :empty-text="emptyText"
    :props="defaultProps"
    :show-checkbox="multiple"
    :node-key="defaultProps.id"
    :check-strictly="checkStrictly"
    :default-checked-keys="defaultCheckedKeys"
    :load="loadNode"
    :filter-node-method="filterNode"
    @check="treeCheck"
    @current-change="treeChange"
  />
</template>

<script>
import request from "../../scripts/request";

export default {
  props: {
    // 双向绑定值
    value: {
      type: [String, Number, Array],
      default: null,
    },
    // 树形结构接口地址
    apiUrl: {
      type: String,
      default: "",
    },
    // 默认与原始数据融合数据
    addData: {
      type: Array,
      default() {
        return [];
      },
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    // 父子不互相关联
    checkStrictly: {
      type: Boolean,
      default: false,
    },
    // 筛选文本
    filterText: {
      type: String,
      default: "",
    },
    halfCheckIds: {
      type: Array,
      default: () => [],
    },
    // 父级节点id
    parentId: {
      type: [String, Number],
      default: "",
    },
    // 是否初始化请求数据
    isInit: {
      type: Boolean,
      default: true,
    },
    // 下拉框回显数据源
    options: {
      type: Array,
      default() {
        return [];
      },
    },
    // 数据选择组件弹框显隐
    visible: {
      type: Boolean,
      default: false,
    },
    // 是否异步
    isAsync: {
      type: Boolean,
      default: true,
    },
    // 树组件配置
    defaultProps: {
      type: Object,
      required: true,
    },
    query: {
      type: Object,
      required: true,
    },
    reload: {
      type: Boolean,
      default: false,
    },
    isPortal: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    noLabs: {
      //树形结构不是懒加载且有value值却没有回显数据源
      type: [Boolean, Number],
      default: false,
    },
    emptyText: {
      type: String,
      default: "暂无数据",
    },
    isRemoteSearch: {
      //下拉框中搜索是否为调用接口远程搜索
      type: Boolean,
      default: false,
    },
    remoteSearchUrl: {
      //下拉框中远程搜索接口
      type: String,
      default: "",
    },
    remoteSearchKey: {
      //下拉框中远程搜索参数名
      type: String,
      default: "keyWord",
    },
  },
  data() {
    return {
      selected: this.value,
      data: [], // 树形结构数据源
      treeLoad: false, //判断接口是否调用完毕
    };
  },
  computed: {
    // 默认勾选树节点设置
    defaultCheckedKeys() {
      return this.multiple ? this.value : [];
    },
  },
  watch: {
    // 树形结构筛选
    filterText(val) {
      if (!this.isRemoteSearch) {
        this.$refs.tree.filter(val);
      }
    },
    // 监听父节点id请求下级节点
    parentId: {
      deep: true,
      immediate: true,
      handler(val) {
        if (val) {
          this.getOperateTree(val);
        }
      },
    },
    //监听接口数据是否需要刷新
    reload: {
      handler(val) {
        if (val && !this.parentId && !this.disabled) {
          this.getOperateTree();
        }
      },
    },
    query: {
      deep: true,
      handler(val) {
        if (!this.isInit) {
          if (Object.keys(val).length > 0) {
            this.getOperateTree();
          } else {
            this.data = [];
          }
        }
      },
    },
    apiUrl: {
      // deep: true,
      // immediate: true,
      handler(val) {
        if (val) {
          this.getOperateTree();
        }
      },
    },
    // 监听选中值及树结构数据获取回显值
    value(val) {
      // 初始化操作
      if (
        typeof this.value === "object" &&
        this.value?.length === 0 &&
        this.selected.length > 0
      ) {
        this.clearSelected();
      }
      this.selected = val;
      // this.setOptions();
      // 父子不互相关联
      if (this.checkStrictly) {
        this.$refs.tree.setCheckedKeys(this.selected);
      }
    },
  },
  created() {
    // 是否默认请求数据
    if (this.isInit) this.getOperateTree();
    this.$nextTick(() => {
      this.$refs.tree.store.lazy = this.isAsync;
    });
  },
  methods: {
    // 树节点筛选事件
    filterNode(value, data) {
      if (!value) return true;
      return data[this.defaultProps.label].indexOf(value) !== -1;
    },
    // 树形组件异步加载方法
    loadNode(node, resolve) {
      if (node.level === 0) return;
      this.getOperateTree(node.data[this.defaultProps.id], resolve);
    },
    // 清空树节点勾选数据
    clearSelected() {
      const isArray = typeof this.value === "object";
      this.$refs.tree[isArray ? "setCheckedKeys" : "setCurrentKey"](
        isArray ? [] : null
      );
    },
    getHalfCheckedKeys() {
      return this.$refs.tree.getHalfCheckedKeys();
    },
    // 树形结构复选框勾选状态变化触发事件
    treeCheck(node) {
      // 获取当前树选中节点并传出
      const checkedKeys = this.$refs.tree.getCheckedKeys();
      const checkedNodes = this.$refs.tree.getCheckedNodes();
      const options = [...checkedNodes];
      this.selected = checkedKeys;
      // 暴露出所有选中树节点
      this.$emit("getAllCheck", checkedNodes);
      // 处理后选中数据，即全选父节点只保存父节点key
      this.$emit("input", this.selected);
      this.$emit("select", node);
      this.$emit("update:options", options);
    },
    // 树节点选中事件
    treeChange(val) {
      if (this.multiple) return;
      const options = [{ ...val }];
      this.selected = val[this.defaultProps.id];
      this.$emit("update:options", options);
      this.$emit("input", this.selected);
      this.$emit("select", val);
      this.$emit("update:visible", false);
    },
    // 设置树节点勾选状态
    setNodeChecked(key, checked) {
      if (this.checkStrictly) {
        this.$refs.tree.setCheckedKeys(this.selected, checked);
      } else {
        // 递归设置子节点勾选状态
        const node = this.$refs.tree.getNode(key);
        this.$refs.tree.setChecked(node, checked);
        if (node.childNodes) this.childChecked(node.childNodes, checked);
      }
    },
    // 递归设置树节点子孙节点勾选状态
    childChecked(list, checked) {
      list.forEach((item) => {
        this.$refs.tree.setChecked(item, checked);
        if (item.childNodes) this.childChecked(item.childNodes);
      });
    },
    // 获取树形结构数据
    getOperateTree(parentId, resolve) {
      if (!this.treeLoad) {
        this.treeLoad = true;
        if (!this.apiUrl) return;
        if (parentId === -1) parentId = "";
        let query = JSON.parse(JSON.stringify(this.query));
        query.parentUuid = parentId ? parentId : query.parentUuid;
        if (this.isRemoteSearch) {
          query[this.remoteSearchKey] = this.filterText;
        }
        let data = {
          url: this.apiUrl,
          method: "get",
          params: query,
        };
        if (this.isPortal) {
          data.baseURL = process.env.VUE_APP_PORTAL_PRE;
        }
        request(data)
          .then((res) => {
            let data = res.data || [];
            let new_data = [];
            if (data) {
              if (data instanceof Array) {
                new_data = res.data;
              } else {
                new_data = data.records || [];
              }
            }
            if (this.isAsync && resolve) {
              resolve(new_data);
            } else {
              this.data = this.addData.concat(new_data);
              if (this.noLabs && !this.isAsync) {
                //树形结构不是懒加载且有value值却没有回显数据源时，会出现回显问题，需获取回显数据
                this.$nextTick(() => {
                  if (this.$refs.tree.getNode(this.value)) {
                    let tree_data = this.$refs.tree.getNode(this.value).data;
                    this.$emit("update:options", [tree_data]);
                  }
                });
              }
            }
            this.setTreeChecked();
            this.treeLoad = false;
          })
          .catch(() => {
            this.treeLoad = false;
          });
      }
    },
    setTreeChecked() {
      this.$nextTick(() => {
        if (this.multiple) this.$refs.tree.setCheckedKeys(this.value);
        if (this.halfCheckIds && this.halfCheckIds.length > 0) {
          this.halfCheckIds.forEach((item) => {
            let node = this.$refs.tree.getNode(item);
            if (node) this.$set(node, "indeterminate", true);
          });
        }
      });
    },
  },
};
</script>

<style scoped></style>
